import type { AnalysisCalendarPeriod, AnalysisDateRange } from "./analysis-date-range";
import { defaultAnalysisDateRange, testAnalysisDateRange } from "./analysis-default-range";
import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import type { ShiftFormManagerDashboard, ShiftFormManagerDashboardFilters, ShiftProductionDepartmentName, ShiftProductionForm, ShiftProductionLineName } from "../../../shared/shift-production-form/contracts";
import { analysisLineDepartment } from "./analysis-departments";
import { useAnalysisFactoryReports } from "./useAnalysisFactoryReports";
import { getShiftForm, getShiftFormManagerDashboard } from "../shift-production-form/shift-production-form-gateway";
import {
  analysisDepartmentLocation,
  analysisGlobalShiftLocation,
  analysisLineLocation,
  analysisLocationFilters,
  analysisRootLocation,
  analysisShiftLocation,
  isAnalysisTerminal,
  type AnalysisLocation,
} from "./analysis-navigation";



function permittedAnalysisLines(dashboard: ShiftFormManagerDashboard | null) {
  return new Set([
    ...(dashboard?.accessibleLineNames ?? []),
    ...(dashboard?.analytics.productionComparison?.machines.map(machine => machine.lineName) ?? []),
  ]);
}

export function useProductionAnalysisWorkspace(actorScope: string, testMode = false) {
  const [filters, setFilters] = useState<ShiftFormManagerDashboardFilters>(() => ({ ...(testMode ? testAnalysisDateRange() : defaultAnalysisDateRange()), status: "COMPLETED", limit: 500 }));
  const [calendarPeriod, setCalendarPeriod] = useState<AnalysisCalendarPeriod>({ kind: testMode ? "custom" : "day" });
  const [location, setLocation] = useState<AnalysisLocation>(analysisRootLocation);
  const [navigationDirection, setNavigationDirection] = useState<"forward" | "back" | "refresh">("refresh");
  const factoryReports = useAnalysisFactoryReports(actorScope, filters, calendarPeriod, testMode);
  const atRoot = !location.departmentName && !location.lineName;
  const [dashboard, setDashboard] = useState<ShiftFormManagerDashboard | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ShiftProductionForm | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const request = useRef(0);
  const detailRequest = useRef(0);
  const effectiveFilters = useMemo(() => analysisLocationFilters(filters, location), [filters, location]);
  const activeDashboard = atRoot ? factoryReports.current.dashboard : dashboard;
  const activePhase = atRoot ? factoryReports.current.phase : phase;
  const activeError = atRoot ? factoryReports.current.error : error;
  const reloadDetail = useCallback(async () => {
    if (atRoot) return;
    const version = ++request.current;
    setPhase("loading");
    setError(null);
    try {
      const result = await getShiftFormManagerDashboard(effectiveFilters, testMode);
      if (version !== request.current) return;
      setDashboard(result);
      setPhase("ready");
    } catch (reason) {
      if (version !== request.current) return;
      setError(reason instanceof Error ? reason.message : "Analiz yüklenemedi.");
      setPhase("error");
    }
  }, [atRoot, effectiveFilters, testMode]);
  const reload = useCallback(async () => {
    factoryReports.reload();
    setNavigationDirection("refresh");
    await reloadDetail();
  }, [factoryReports.reload, reloadDetail]);
  useEffect(() => {
    if (atRoot) return;
    setPhase("loading");
    void reloadDetail();
    return () => { request.current += 1; };
  }, [atRoot, reloadDetail]);
  useEffect(() => () => { detailRequest.current += 1; }, []);
  const closeDetailForScopeChange = useCallback(() => {
    detailRequest.current += 1;
    setSelected(null);
    setDetailBusy(false);
    setDetailError(null);
  }, []);
  const invalidateScope = useCallback(() => {
    request.current += 1;
    setDashboard(null);
    setPhase("loading");
    setError(null);
    closeDetailForScopeChange();
  }, [closeDetailForScopeChange]);
  const setReportFilters: Dispatch<SetStateAction<ShiftFormManagerDashboardFilters>> = useCallback((next) => {
    invalidateScope();
    setNavigationDirection("refresh");
    setFilters(current => typeof next === "function" ? next(current) : next);
  }, [invalidateScope]);
  const selectDateRange = useCallback((range: AnalysisDateRange, period: AnalysisCalendarPeriod) => {
    setCalendarPeriod(period);
    setReportFilters(current => ({ ...current, dateFrom: range.dateFrom, dateTo: range.dateTo }));
  }, [setReportFilters]);
  const selectDepartment = useCallback((departmentName: ShiftProductionDepartmentName) => {
    invalidateScope();
    setNavigationDirection("forward");
    setLocation(analysisDepartmentLocation(departmentName));
  }, [invalidateScope]);
  const selectMachine = useCallback((lineName: ShiftProductionLineName, departmentName?: ShiftProductionDepartmentName) => {
    const resolvedDepartment = departmentName ?? (activeDashboard ? analysisLineDepartment(activeDashboard, lineName) : undefined);
    if (!resolvedDepartment || !permittedAnalysisLines(activeDashboard).has(lineName)) return;
    invalidateScope();
    setNavigationDirection("forward");
    setLocation(analysisLineLocation(resolvedDepartment, lineName));
  }, [activeDashboard, invalidateScope]);
  const selectShift = useCallback((lineName: ShiftProductionLineName, shift: NonNullable<ShiftFormManagerDashboardFilters["shift"]>) => {
    if (location.lineName !== lineName || (filters.shift && filters.shift !== shift)) return;
    invalidateScope();
    setNavigationDirection("forward");
    setLocation(current => analysisShiftLocation(current, shift));
  }, [filters.shift, invalidateScope, location.lineName]);
  const selectGlobalShift = useCallback((shift?: NonNullable<ShiftFormManagerDashboardFilters["shift"]>) => {
    invalidateScope();
    setNavigationDirection("refresh");
    setFilters(current => ({ ...current, shift }));
    setLocation(analysisGlobalShiftLocation);
  }, [invalidateScope]);
  const goToLevel = useCallback((level: "ROOT" | "DEPARTMENT" | "LINE") => {
    invalidateScope();
    setNavigationDirection("back");
    setLocation(current => {
      if (level === "ROOT") return analysisRootLocation();
      if (level === "DEPARTMENT") return current.departmentName ? analysisDepartmentLocation(current.departmentName) : analysisRootLocation();
      return current.lineName && current.departmentName ? analysisLineLocation(current.departmentName, current.lineName) : analysisRootLocation();
    });
  }, [invalidateScope]);
  const selectMachineShortcut = useCallback((lineName?: ShiftProductionLineName) => {
    if (!lineName) {
      goToLevel("ROOT");
      return;
    }
    selectMachine(lineName);
  }, [goToLevel, selectMachine]);
  useEffect(() => {
    if (phase !== "ready" || !dashboard || !location.lineName) return;
    if (permittedAnalysisLines(dashboard).has(location.lineName)) return;
    invalidateScope();
    setLocation(location.departmentName ? analysisDepartmentLocation(location.departmentName) : analysisRootLocation());
  }, [dashboard, invalidateScope, location.departmentName, location.lineName, phase]);
  const openForm = async (id: string) => {
    const version = ++detailRequest.current;
    setDetailBusy(true);
    setDetailError(null);
    try {
      const result = await getShiftForm(id, testMode);
      if (version === detailRequest.current) setSelected(result.form);
    } catch (reason) {
      if (version === detailRequest.current) setDetailError(reason instanceof Error ? reason.message : "Kaynak form açılamadı.");
    } finally {
      if (version === detailRequest.current) setDetailBusy(false);
    }
  };
  const closeForm = () => { detailRequest.current += 1; setSelected(null); setDetailBusy(false); };
  return {
    filters,
    calendarPeriod,
    onDateRange: selectDateRange,
    setFilters: setReportFilters,
    effectiveFilters,
    location,
    terminal: isAnalysisTerminal(location),
    dashboard: activeDashboard,
    phase: activePhase,
    error: activeError,
    factoryReports,
    navigationDirection,
    reload,
    selected,
    detailBusy,
    detailError,
    openForm,
    closeForm,
    selectDepartment,
    selectMachine,
    selectMachineShortcut,
    selectShift,
    selectGlobalShift,
    goToLevel,
  };
}
